/**
 * The idea cards under a fresh composer, one markup for both surfaces: the
 * canvas landing, where a click prefills the input from the canvas's own
 * four, and the home, where a click starts a canvas with the card's prompt
 * as its first message.
 */

import {
  CANVAS_COMPOSER_SUGGESTIONS,
  type CanvasComposerSuggestion,
} from "@/pages/canvas/config/canvasComposerSuggestions";
import { cn } from "@/utils";

export interface ComposerSuggestionsProps {
  onSelect: (suggestion: CanvasComposerSuggestion) => void;
  suggestions?: readonly CanvasComposerSuggestion[];
  /** The grid's columns; two on the landing, up to three on the home. */
  className?: string;
  /** Classes for one card by position, such as hiding it at some widths. */
  cardClassName?: (index: number) => string;
  disabled?: boolean;
}

export function ComposerSuggestions({
  onSelect,
  suggestions = CANVAS_COMPOSER_SUGGESTIONS,
  className,
  cardClassName,
  disabled = false,
}: ComposerSuggestionsProps) {
  return (
    <div
      data-testid="composer-suggestions"
      className={cn("grid w-full gap-2.5", className)}
    >
      {suggestions.map((s, index) => {
        const Icon = s.icon;
        return (
          <button
            key={s.id}
            type="button"
            disabled={disabled}
            // Keep the editor focused so the prefilled text lands ready to send.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(s)}
            className={cn(
              "group flex cursor-pointer items-start gap-3 rounded-xl border border-border/70 bg-surface-card/60 p-3 text-left",
              "transition-all duration-150 ease-out hover:border-border hover:bg-surface-card hover:shadow-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "disabled:cursor-default disabled:opacity-60",
              cardClassName?.(index),
            )}
          >
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-colors group-hover:text-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-medium text-foreground">
                {s.label}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {s.hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
